import React, { useState } from 'react';
import api from '../utils/api';

/**
 * Feedback — a small button on every page for beta testers.
 *
 * Four kinds, because "report a bug" only ever gets you bugs. The requests and
 * the "I could not find where to do this" reports are the ones worth having.
 */

const KINDS = [
  {
    id: 'bug',
    label: 'Something broke',
    icon: '⚑',
    title: 'Something broke',
    blurb: 'Rough and quick is fine. This is the useful part.',
    q1: 'What happened?',
    p1: 'Logged a worming and it spun, then the record was gone',
    q2: 'What did you expect?',
    p2: 'To see the next due date',
  },
  {
    id: 'confusing',
    label: 'This is confusing',
    icon: '?',
    title: 'This is confusing',
    blurb: 'Not everything wrong is broken. This kind of report is gold.',
    q1: 'What were you trying to do?',
    p1: 'Log a dental for Biscuit — I could not find where',
    q2: 'Where did you look first?',
    p2: 'Under the horse, then under calendar',
  },
  {
    id: 'request',
    label: 'I need a feature',
    icon: '+',
    title: 'Ask for a feature',
    blurb: 'Tell me what your barn needs. This list decides what I build next.',
    q1: 'What do you need it to do?',
    p1: 'Track lease agreements per horse with an end date',
    q2: 'How do you handle it today?',
    p2: 'Paper in a folder, and I forget the renewal every year',
  },
  {
    id: 'missing',
    label: 'Something is missing',
    icon: '◦',
    title: 'Something is missing',
    blurb: 'A field, a screen, a bit of information that should be there.',
    q1: 'What is missing?',
    p1: 'No way to record a horse height anywhere',
    q2: 'Where would you expect to find it?',
    p2: 'On the horse edit page with colour and markings',
  },
  {
    id: 'other',
    label: 'Something else',
    icon: '…',
    title: 'Something else',
    blurb: 'Praise, a question, an idea, or none of the above.',
    q1: 'What is on your mind?',
    p1: 'Anything at all',
    q2: 'Anything else? (optional)',
    p2: '',
  },
];

export default function ReportIssue() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [shot, setShot] = useState(null);
  const [includeShot, setIncludeShot] = useState(true);
  const [a1, setA1] = useState('');
  const [a2, setA2] = useState('');
  const [error, setError] = useState('');

  const capture = async () => {
    try {
      const mod = await import('html2canvas');
      const html2canvas = mod.default || mod;
      const canvas = await html2canvas(document.body, {
        logging: false, useCORS: true, scale: 0.6, backgroundColor: '#FBF8F3',
        windowWidth: document.documentElement.clientWidth,
        height: Math.min(document.body.scrollHeight, 2400),
      });
      return canvas.toDataURL('image/png');
    } catch (e) { return null; }
  };

  const start = async () => {
    setError(''); setSent(false); setKind(null);
    setA1(''); setA2(''); setIncludeShot(true); setShot(null);
    const img = await capture();     // before the sheet covers the page
    setShot(img);
    setOpen(true);
  };

  const send = async () => {
    if (!a1.trim()) { setError('A line or two is plenty — anything is better than nothing.'); return; }
    setBusy(true); setError('');
    try {
      await api.post('/feedback', {
        report_type: kind.id,
        what_happened: a1,
        expected: a2,
        page_url: window.location.pathname,
        page_title: document.title,
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screenshot: includeShot ? shot : null,
      });
      setSent(true);
    } catch (e) {
      setError('That did not send. Try again in a moment.');
    } finally { setBusy(false); }
  };

  const label = { display: 'block', fontSize: 11, letterSpacing: '.05em',
                  textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 5 };

  return (
    <>
      <button onClick={start} aria-label="Send feedback"
        style={{ position: 'fixed', right: 14, bottom: 74, zIndex: 900,
                 background: 'var(--cr-primary)', color: '#fff', border: 0,
                 borderRadius: 22, padding: '9px 15px', fontSize: 12, fontWeight: 600,
                 boxShadow: '0 6px 16px rgba(30,3,17,.35)', cursor: 'pointer' }}>
        &#9873; Feedback
      </button>

      {open && (
        <div onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}
             style={{ position: 'fixed', inset: 0, background: 'rgba(30,3,17,.45)', zIndex: 1000,
                      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 520,
                        borderRadius: '18px 18px 0 0', padding: 18,
                        maxHeight: '92vh', overflowY: 'auto' }}>

            {sent ? (
              <div style={{ textAlign: 'center', padding: '26px 10px 18px' }}>
                <div style={{ fontSize: 34, marginBottom: 10 }}>&#10003;</div>
                <h3 style={{ marginBottom: 8 }}>Thank you &mdash; that is genuinely useful.</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
                  I read every one of these myself. If it needs a reply you will get one.
                </p>
                <button className="btn btn-primary btn-full" onClick={() => setOpen(false)}>
                  Back to what I was doing
                </button>
              </div>
            ) : !kind ? (
              <>
                <h3 style={{ marginBottom: 3 }}>Tell me something</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 14 }}>
                  Bugs are welcome. So is everything else.
                </p>
                {KINDS.map(k => (
                  <button key={k.id} onClick={() => setKind(k)}
                    style={{ width: '100%', textAlign: 'left', background: '#fff',
                             border: '1px solid var(--line, #E4DCCE)', borderRadius: 10,
                             padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
                             display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                                   background: 'var(--surface-2, #F3EEE6)', display: 'flex',
                                   alignItems: 'center', justifyContent: 'center',
                                   fontSize: 15, color: 'var(--cr-primary)' }}>{k.icon}</span>
                    <span>
                      <span style={{ fontWeight: 600, fontSize: 14, display: 'block' }}>{k.label}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{k.blurb}</span>
                    </span>
                  </button>
                ))}
                <button className="btn btn-secondary btn-full" style={{ marginTop: 6 }}
                        onClick={() => setOpen(false)}>Cancel</button>
              </>
            ) : (
              <>
                <button onClick={() => setKind(null)}
                        style={{ background: 'none', border: 0, padding: 0, marginBottom: 8,
                                 color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>
                  &larr; All feedback types
                </button>
                <h3 style={{ marginBottom: 3 }}>{kind.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
                  {kind.blurb}
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {[document.title || 'This page',
                    `${window.innerWidth}x${window.innerHeight}`,
                    new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                  ].map(t => (
                    <span key={t} style={{ background: 'var(--surface-2, #F3EEE6)',
                      border: '1px solid var(--line, #E4DCCE)', borderRadius: 11,
                      padding: '3px 9px', fontSize: 11, color: 'var(--text-secondary)' }}>{t}</span>
                  ))}
                </div>

                <label style={label}>{kind.q1}</label>
                <textarea className="form-input" rows={3} value={a1} autoFocus
                  placeholder={kind.p1} onChange={e => setA1(e.target.value)}
                  style={{ marginBottom: 12 }} />

                <label style={label}>{kind.q2} (optional)</label>
                <textarea className="form-input" rows={2} value={a2}
                  placeholder={kind.p2} onChange={e => setA2(e.target.value)}
                  style={{ marginBottom: 12 }} />

                {shot && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                                border: '1px solid var(--line, #DDD6CC)', borderRadius: 9,
                                padding: 8, marginBottom: 14 }}>
                    <img src={shot} alt="" style={{ width: 46, height: 60, objectFit: 'cover',
                         objectPosition: 'top', borderRadius: 4, border: '1px solid #DDD6CC' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>Include screenshot</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        Taken when you tapped the button
                      </div>
                    </div>
                    <input type="checkbox" checked={includeShot}
                           onChange={e => setIncludeShot(e.target.checked)}
                           style={{ width: 20, height: 20 }} />
                  </div>
                )}

                {error && <div style={{ color: '#9B2C2C', fontSize: 13, marginBottom: 10 }}>{error}</div>}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }}
                          onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 2 }}
                          onClick={send} disabled={busy}>
                    {busy ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
